import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function CrmClienteDetalle() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [notas, setNotas] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get(`/clientes/${id}`)
            .then((r) => {
                setData(r.data);
                setNotas(r.data.cliente.notas || "");
            })
            .catch((err) => toast.error(formatApiError(err)));
    }, [id]);

    const saveNotas = async () => {
        setSaving(true);
        try {
            await api.patch(`/clientes/${id}`, { notas });
            toast.success("Notas guardadas");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    if (!data) return <p className="text-piedra italic font-body">Cargando...</p>;
    const { cliente, reservas, eventos } = data;

    return (
        <div className="space-y-12">
            <Link
                to="/crm/clientes"
                className="inline-flex items-center gap-2 text-piedra hover:text-dorado text-xs uppercase tracking-[0.25em] font-body"
            >
                <ArrowLeft size={14} /> Clientes
            </Link>

            <header>
                <p className="font-body uppercase tracking-[0.3em] text-xs text-dorado mb-3">
                    Cliente
                </p>
                <h1 className="font-heading text-5xl text-crema font-light">{cliente.nombre}</h1>
                <p className="font-body text-piedra mt-3">
                    {cliente.email || "—"} · {cliente.telefono || "—"}
                </p>
            </header>

            <section className="border border-dorado/20 bg-carbon-suave/40 p-8">
                <label className="line-label">Notas internas</label>
                <textarea
                    rows={4}
                    value={notas}
                    onChange={(e) => setNotas(e.target.value)}
                    className="input-line resize-none"
                    data-testid="cliente-notas"
                />
                <button onClick={saveNotas} disabled={saving} className="btn-gold mt-4">
                    {saving ? "Guardando..." : "Guardar notas"}
                </button>
            </section>

            <section>
                <h2 className="font-heading text-3xl text-crema mb-6">Reservas</h2>
                {reservas.length === 0 ? (
                    <p className="text-piedra italic font-body">Sin reservas.</p>
                ) : (
                    <div className="border border-dorado/20 bg-carbon-suave/40 divide-y divide-dorado/10">
                        {reservas.map((r) => (
                            <div
                                key={r.id}
                                className="px-5 py-4 grid grid-cols-1 md:grid-cols-4 gap-3"
                            >
                                <span className="font-body text-sm text-crema">{r.fecha}</span>
                                <span className="font-heading italic text-dorado">{r.hora}</span>
                                <span className="font-body text-sm text-piedra">{r.personas} pax</span>
                                <span className="font-body text-xs uppercase tracking-[0.2em] text-piedra">
                                    {r.estado}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <section>
                <h2 className="font-heading text-3xl text-crema mb-6">Eventos</h2>
                {eventos.length === 0 ? (
                    <p className="text-piedra italic font-body">Sin eventos.</p>
                ) : (
                    <div className="border border-dorado/20 bg-carbon-suave/40 divide-y divide-dorado/10">
                        {eventos.map((e) => (
                            <Link
                                key={e.id}
                                to={`/crm/eventos/${e.id}`}
                                className="px-5 py-4 grid grid-cols-1 md:grid-cols-4 gap-3 hover:bg-carbon"
                            >
                                <span className="font-heading text-lg text-crema">{e.tipo}</span>
                                <span className="font-body text-sm text-piedra">{e.fecha || "—"}</span>
                                <span className="font-body text-sm text-piedra">{e.personas || "—"} pax</span>
                                <span className="font-body text-xs uppercase tracking-[0.2em] text-piedra">
                                    {e.estado}
                                </span>
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
